import { AttrValue, AttrValueOptions } from 'models/flexible/AttrValue';
import { Classification, ClassificationOptions } from 'models/flexible/Classification';
import { Group, GroupOptions } from 'models/flexible/Group';
import { User, UserOptions } from 'models/flexible/User';
import type { Typed } from 'services/api/fields/core';

import { GroupType, GroupTypeOptions } from './GroupType';

export enum NotificationChannel {
  EMAIL = 'email',
}

export type NotificationChannels = {
  [key in NotificationChannel]: boolean;
};

export interface NotificationsSelections {
  activity: Record<string, NotificationChannels>;
  criticalAlerts: Record<string, NotificationChannels>;
  patientStatusChanges: Record<string, NotificationChannels>;
}

export interface NotificationOptions {
  groupType?: GroupTypeOptions;
  scopes?: Partial<{
    episodes: ClassificationOptions[];
    groups: GroupOptions[];
    planTypes: ClassificationOptions[];
    caseManagers: UserOptions[];
    utilizationManagers: UserOptions[];
    physicianTeams: AttrValueOptions[];
  }>;
  [key: string]: any;
}

const getDefaults = (): NotificationOptions => ({
  id: '',
  userId: '',
  clientId: '',
  active: false,
  groupTypeApiName: '',
  notificationSelections: {
    activity: {},
    criticalAlerts: {},
    patientStatusChanges: {},
  },
  createdAt: new Date(),
  updatedAt: new Date(),
  type: '',
  scopes: {
    episodes: [],
    groups: [],
    planTypes: [],
    caseManagers: [],
    utilizationManagers: [],
    physicianTeams: [],
  },
});

let Notification: new <T extends NotificationOptions>(options: T) => Typed<_Notification<T>, T>;

class _Notification<T extends NotificationOptions = NotificationOptions> {
  groupType!: GroupType;
  scopes!: Partial<{
    episodes: Classification[];
    groups: Group[];
    planTypes: Classification[];
    caseManagers: User[];
    utilizationManagers: User[];
    physicianTeams: AttrValue[];
  }>;

  constructor(options: T) {
    const opts = { ...getDefaults(), ...options };
    Object.assign(this, opts);

    this.groupType = new GroupType(opts.groupType);
    this.scopes = {
      episodes: opts.scopes?.episodes?.map((episode) => new Classification(episode)),
      groups: opts.scopes?.groups?.map((group) => new Group(group)),
      planTypes: opts.scopes?.planTypes?.map((planType) => new Classification(planType)),
      caseManagers: opts.scopes?.caseManagers?.map((caseManager) => new User(caseManager)),
      utilizationManagers: opts.scopes?.utilizationManagers?.map((utilizationManager) => new User(utilizationManager)),
      physicianTeams: opts.scopes?.physicianTeams?.map((physician) => new AttrValue(physician)),
    };
  }
}

// eslint-disable-next-line prefer-const
Notification = _Notification as typeof Notification;

export default Notification;
export { Notification };
export type Notification<T extends NotificationOptions = NotificationOptions> = Typed<_Notification<T>, T>;
